export interface ThemeConfig {
  name: string;
  mode: 'light' | 'dark';
  colors?: {
    primary?: string;
    primaryHover?: string;
    primaryActive?: string;
    secondary?: string;
    secondaryHover?: string;
    secondaryActive?: string;
    success?: string;
    warning?: string;
    error?: string;
    info?: string;
    background?: string;
    foreground?: string;
    surface?: string;
    surfaceHover?: string;
    text?: string;
    textSecondary?: string;
    textMuted?: string;
    textDisabled?: string;
    border?: string;
    borderLight?: string;
    [key: string]: string | undefined;
  };
  typography?: {
    fontFamily?: Record<string, string>;
    fontSize?: Record<string, string>;
    fontWeight?: Record<string, number>;
    lineHeight?: Record<string, number>;
  };
  spacing?: Record<string, string>;
  breakpoints?: Record<string, string>;
  borderRadius?: Record<string, string>;
  shadows?: Record<string, string>;
  transitions?: Record<string, string>;
  zIndex?: Record<string, number>;
}

/**
 * Applies theme colors and properties to CSS custom properties for Tailwind integration
 */
export function applyThemeToCSS(theme: ThemeConfig): void {
  if (!theme.colors) return;

  const root = document.documentElement;

  // Apply color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    if (typeof value === 'string') {
      root.style.setProperty(`--color-${key.toLowerCase()}`, value);
    }
  });

  // Apply typography variables
  if (theme.typography) {
    if (theme.typography.fontFamily) {
      Object.entries(theme.typography.fontFamily).forEach(([key, value]) => {
        root.style.setProperty(`--font-family-${key}`, value);
      });
    }

    if (theme.typography.fontSize) {
      Object.entries(theme.typography.fontSize).forEach(([key, value]) => {
        root.style.setProperty(`--font-size-${key}`, value);
      });
    }

    if (theme.typography.fontWeight) {
      Object.entries(theme.typography.fontWeight).forEach(([key, value]) => {
        root.style.setProperty(`--font-weight-${key}`, value.toString());
      });
    }

    if (theme.typography.lineHeight) {
      Object.entries(theme.typography.lineHeight).forEach(([key, value]) => {
        root.style.setProperty(`--line-height-${key}`, value.toString());
      });
    }
  }

  // Apply spacing variables
  if (theme.spacing) {
    Object.entries(theme.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-theme-${key}`, value);
    });
  }

  // Apply border radius variables
  if (theme.borderRadius) {
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-theme-${key}`, value);
    });
  }

  // Apply shadow variables
  if (theme.shadows) {
    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-theme-${key}`, value);
    });
  }

  // Apply transition variables
  if (theme.transitions) {
    Object.entries(theme.transitions).forEach(([key, value]) => {
      root.style.setProperty(`--transition-theme-${key}`, value);
    });
  }

  // Apply z-index variables
  if (theme.zIndex) {
    Object.entries(theme.zIndex).forEach(([key, value]) => {
      root.style.setProperty(`--z-index-theme-${key}`, value.toString());
    });
  }
}

/**
 * Generates hover and active color variants for a given color
 */
export function generateColorVariants(baseColor: string): {
  hover: string;
  active: string;
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
} {
  // For now, return placeholder variants
  // In a real implementation, you'd use a color manipulation library
  // like chroma-js or colorjs.io to generate proper variants
  return {
    hover: baseColor, // TODO: Implement proper hover color generation
    active: baseColor, // TODO: Implement proper active color generation
    50: baseColor,
    100: baseColor,
    200: baseColor,
    300: baseColor,
    400: baseColor,
    500: baseColor,
    600: baseColor,
    700: baseColor,
    800: baseColor,
    900: baseColor,
  };
}

/**
 * Utility function to combine Tailwind classes with inline styles
 * Prioritizes Tailwind classes, falls back to inline styles
 */
export function combineStyles(tailwindClasses: string, inlineStyles?: React.CSSProperties): React.CSSProperties {
  const combined: React.CSSProperties = { ...inlineStyles };

  // Add a comment to indicate Tailwind is preferred
  if (tailwindClasses) {
    (combined as any)['--tailwind-classes'] = tailwindClasses;
  }

  return combined;
}

/**
 * Hook to get theme-aware class names
 * Combines theme-specific classes with component classes
 */
export function useThemeClasses(componentClasses: string, themeOverrides?: string): string {
  // For now, just combine the classes
  // In the future, this could be enhanced to handle theme-specific logic
  return [componentClasses, themeOverrides].filter(Boolean).join(' ');
}
